import { Button } from "@/components/ui/Button";
import classes from "./Form.module.scss";
import { useTranslation } from "@/hooks/useTranslation";
import { DonationSourceWithConfig } from "@/components/DonationSource";
import { SignupToFrontlineWithConfig } from "@/components/SignupToFrontline";
import { useDonationFormStore } from "@/stores";
import { useImperativeHandle, useRef } from "react";
import type { FormEvent } from "react";
import type { ImperativeHandle } from "@/types/Form";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import { DetailsStep } from "@/types/Navigation";
import { useFormComponentConfig } from "@/hooks/useFormComponentConfig";

type FormStepInformationProps = {
  onStepSubmit: () => void;
  ref?: React.Ref<ImperativeHandle>;
};

export function FormStepInformation(props: FormStepInformationProps) {
  const { onStepSubmit, ref } = props;
  const t = useTranslation("_common");
  const { donationSource, onBehalf, organisationName, isUKTaxPayer, ...rest } =
    useDonationFormStore();

  const donationSourceRef = useRef<ImperativeHandle>(null);
  const donationSourceComponent = useFormComponentConfig("donation_source");

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (!donationSourceRef.current) {
        return {
          isValid: true,
        };
      }

      return donationSourceRef.current.validate();
    },
  }));

  // The organisation type select renders only for an option with sub-options,
  // so an option without them (`has-sub-options` off) must not wait for it.
  const selectedOption = donationSourceComponent?.config[
    "donation-source-options"
  ].find((option) => option.value === donationSource);
  const needsOnBehalf = (selectedOption?.["sub-options"]?.length ?? 0) > 0;
  const showSubmitButton = !!donationSource && (!needsOnBehalf || !!onBehalf);

  const hanldeSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const validation = await donationSourceRef.current?.validate();
    if (validation?.isValid) {
      onStepSubmit();
      dispatchEvent(EventName.STEP_SUBMIT, {
        step_name: DetailsStep.DonationInformation,
        data: {
          uk_taxpayer: isUKTaxPayer,
          sign_up_frontline: rest.signUpFrontline,
          on_behalf: onBehalf,
          organisation_name: organisationName,
        },
      });
    }
  };

  return (
    <form onSubmit={hanldeSubmit}>
      <div className={classes.informationGroup}>
        <DonationSourceWithConfig ref={donationSourceRef} />

        <SignupToFrontlineWithConfig />
      </div>

      {showSubmitButton && (
        <div className={classes.pagination}>
          <Button size="large" variant="outlined" type="submit">
            {t("continue")}
          </Button>
        </div>
      )}
    </form>
  );
}
